import React, { FC, useCallback, useContext, useRef } from 'react';
import { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import { ZoomContext } from 'context/ZoomContext';

export interface EChartsContainerProps {
  option: EChartsOption;
  className?: string;
  style?: React.CSSProperties;
  error?: string | Error | null;
  loading?: boolean;
  notMerge?: boolean;
  onEvents?: Record<string, (params: unknown) => void>;
  onChartReady?: (chart?: unknown) => void;
}

const defaultChartStyle = { height: '400px', width: '100%' };

export const EChartsContainer: FC<EChartsContainerProps> = ({
  option,
  className,
  style,
  error = null,
  loading = false,
  notMerge = false,
  onEvents,
  onChartReady,
}) => {
  const observerRef = useRef<ResizeObserver | null>(null);
  const zoomFixCleanupRef = useRef<(() => void) | null>(null);

  // Use a ref so the capture-phase listener always reads the current zoom level
  // without needing to be recreated on every zoom change.
  const zoomCtx = useContext(ZoomContext);
  const zoomLevelRef = useRef<number>(1);
  zoomLevelRef.current = zoomCtx?.zoomLevel ?? 1;

  const handleChartReady = useCallback((chart: any) => {
    // Observe the chart's DOM container so that any layout shift (sidebar toggle,
    // scroll container reflow, etc.) causes ECharts to recalculate its bounding rect.
    // Without this, the crosshair/tooltip coordinate mapping gets a stale left offset.
    const container = chart.getDom?.() as HTMLElement | undefined;
    if (container) {
      zoomFixCleanupRef.current?.();
      observerRef.current?.disconnect();
      const observer = new ResizeObserver(() => chart.resize());
      observer.observe(container);
      observerRef.current = observer;

      // Fix crosshair/tooltip alignment when a CSS zoom is applied to an ancestor.
      //
      // ZRender normalizes mouse events as:
      //   zrX = clientX - getBoundingClientRect().left  (viewport pixels, affected by ancestor zoom)
      // ECharts' grid coordinate system is based on offsetWidth              (CSS pixels, unaffected).
      // At zoom 0.8 these diverge by a factor of 0.8, causing progressive drift left→right.
      //
      // ZRender skips its own normalization when zrX is already set (`if (e.zrX != null) return e`).
      // We pre-set zrX/zrY in the capture phase so our corrected value wins.
      const zoomFixListener = (e: MouseEvent) => {
        const rect = container.getBoundingClientRect();
        (e as any).zrX = (e.clientX - rect.left) / zoomLevelRef.current;
        (e as any).zrY = (e.clientY - rect.top) / zoomLevelRef.current;
      };
      container.addEventListener('mousemove', zoomFixListener, true);
      zoomFixCleanupRef.current = () => container.removeEventListener('mousemove', zoomFixListener, true);
    }
    onChartReady?.(chart);
  }, [onChartReady]);

  if (error) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          color: '#d32f2f',
          ...style,
        }}>
        <div>
          <strong>Error loading chart:</strong> {error instanceof Error ? error.message : String(error)}
        </div>
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      notMerge={notMerge}
      style={style ?? defaultChartStyle}
      className={className}
      onEvents={onEvents}
      showLoading={loading}
      onChartReady={handleChartReady}
      opts={{ renderer: 'svg' }}
    />
  );
};
